import { useEffect, useState } from 'react'

import countriesMap from '@localization/countries.json'
import { useMultiCountryPricingAndAvailabilityLazyQuery } from '@src/queries/MultiCountryPricingAndAvailabilityQuery.generated'
import { PdpFieldsFragment } from '@src/queries/PDPQuery.generated'
import { usePricingAndAvailabilityLazyQuery } from '@src/queries/PricingAndAvailabilityQuery.generated'
import { CatalogType } from '@src/types/graphql-types'
import isbot from '@src/utils/isBot'
import { determineCatalogType } from '@src/utils/searchUtils'
import { useCheckUserCountry } from '@src/utils/useCheckUserCountry'
import { SitePreference, useCurrentUser } from '@src/utils/useCurrentUser'
import { useUserSession } from '@src/utils/useUserSession'

type ProductPricing = Pick<
  PdpFieldsFragment,
  | 'catalogId'
  | 'erp_type'
  | 'isMarketplace'
  | 'materialIds'
  | 'paMessage'
  | 'productKey'
  | 'productNumber'
> & {
  brandKey?: string
}

export const useProductPricing = (product: ProductPricing) => {
  const ua = typeof window !== 'undefined' ? window.navigator.userAgent : ''
  const isBot = isbot(ua)
  const {
    userSession: { country },
  } = useUserSession()

  const {
    isMultiCountryUser,
    isDTAQZuCustomer,
    isBlueErpIntegrationEnabled,
    getSitePreference,
  } = useCurrentUser()

  const [countryCode] = useState(country)
  const isUsUser = useCheckUserCountry(countriesMap.US.countryCode)
  const productNotSoldInCountry = product.isMarketplace && !isUsUser

  const [
    getPricingForProduct,
    {
      data: singleCountryPricing,
      loading: singleCountryLoading,
      error: singleCountryError,
    },
  ] = usePricingAndAvailabilityLazyQuery({
    fetchPolicy: isDTAQZuCustomer ? 'no-cache' : 'cache-first',
  })

  const [
    getPricingForMultiCountryProducts,
    {
      data: multiCountryPricing,
      loading: multiCountryLoading,
      error: multiCountryError,
    },
  ] = useMultiCountryPricingAndAvailabilityLazyQuery()

  const pricingData = isMultiCountryUser
    ? multiCountryPricing?.getPricingForMultiCountryProducts?.find(
        (countryPricingData) => countryPricingData?.country === countryCode
      )
    : singleCountryPricing?.getPricingForProduct

  const erpType = product?.erp_type
  const passERPType =
    isBlueErpIntegrationEnabled && erpType && erpType?.length > 0
  const isMarketplace = product?.isMarketplace
  const displaySDS = product?.productNumber?.startsWith('CB_')
  const orgId = getSitePreference(SitePreference.CatalogFilter)

  const getProductPricing = () => {
    const queryVariables = {
      variables: {
        productNumber: product?.productNumber,
        materialIds: product?.materialIds,
        brand: product?.brandKey,
        quantity: 1,
        catalogType: isMarketplace
          ? CatalogType.Marketplace
          : determineCatalogType(product?.catalogId),
        orgId,
        checkForPb: true,
        displaySDS,
        dealerId: '',
        checkBuyNow: true,
        productKey: product?.productKey,
        cachedPriceOnly: isBot,
        ...(passERPType && { erpType }),
      },
    }

    const updatedQueryVariables = {
      variables: {
        ...queryVariables.variables,
        cachedPriceOnly: isBot,
      },
    }

    if (isMultiCountryUser && !productNotSoldInCountry) {
      getPricingForMultiCountryProducts(queryVariables)
    } else if (!productNotSoldInCountry) {
      getPricingForProduct(updatedQueryVariables)
    }
  }

  useEffect(() => {
    if (product?.productNumber && !product?.paMessage) {
      getProductPricing()
    }
  }, [
    isMultiCountryUser,
    getPricingForMultiCountryProducts,
    getPricingForProduct,
    product?.productNumber,
    product?.paMessage,
  ])

  const { materialPricing, discontinuedPricingInfo } = pricingData ?? {}
  const pricingError = singleCountryError || multiCountryError
  const pricingLoading =
    (singleCountryLoading || multiCountryLoading) && !pricingError

  return {
    materialPricing,
    pricingError,
    pricingLoading,
    countryCode,
    isMultiCountryUser,
    discontinuedPricingInfo,
  }
}
